import { faSquareArrowDownRight } from "@fortawesome/pro-solid-svg-icons/faSquareArrowDownRight";
import { faSquareArrowUp } from "@fortawesome/pro-solid-svg-icons/faSquareArrowUp";
import { Avatar, Box, Breadcrumbs, Card, Dialog, DialogActions, DialogContent, Grid, Pagination, Typography } from "@mui/material";
import { AppImageDimension } from "app/appImages";
import { MprcDialogTitleClose, MprcIconFa, MprcLink, MprcMessageBox, MprcRequestErrorReport } from "components";
import { MprcChipChoice } from "components/MprcChipChoice";
import { MprcInfiniteLoading } from "components/MprcInfiniteLoading";
import { MprcListItemTitle } from "components/MprcListItemTitle";
import { OrganogramManagerDto, SubordinateDto, UserMainManagerSubordinatesResponse, UserManagerTypesResponse, UserType } from "generated/CoreApi_v1";
import { useCoreApi } from "providers/CoreApiProvider";
import { useModalUserProfile } from "providers/UserProfileProvider";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { NIL } from "uuid";
////////////////////////////////////////////////////////////////////////////////

interface IUser {
    userId: string;
    name: string;
    userType: UserType;
}

interface IPros {
    userId: string;
    onClose: (user: IUser | undefined) => void;
}

/////////////////////////////////////////////////////////////////////////////////

export const SubordinatesOrgDialog: React.FC<IPros> = (props) => {
    const api = useCoreApi();
    const { t } = useTranslation();
    const { userId, onClose } = props;

    const [breadrumbs, setBreadrumbs] = useState<OrganogramManagerDto[]>([]);
    const [viewData, setViewData] = useState<UserMainManagerSubordinatesResponse>();
    const [viewError, setViewError] = useState<any>();
    const [loading, setLoading] = useState<boolean>(true);
    const pageNumber = useRef<number>(1);
    const managerType = useRef<string | null>(null);
    const manager = useRef<OrganogramManagerDto | null>(null);

    const { showUser } = useModalUserProfile();

    /////////////////////////////////////////////////////////////////////////////////

    const handlePreviousManager = (newManager: OrganogramManagerDto) => {
        var index = breadrumbs.findIndex(p => p.userId == newManager.userId);
        setBreadrumbs(breadrumbs.filter((_, i) => i <= index));
        manager.current = newManager;
        managerType.current = null;
        pageNumber.current = 1;
        setViewData(undefined);
        setLoading(true);
    };

    const handleNewManager = (newManager: OrganogramManagerDto) => {
        manager.current = newManager;
        managerType.current = null;
        pageNumber.current = 1;
        setViewData(undefined);
        setBreadrumbs([...breadrumbs, newManager]);
        setLoading(true);
    };

    const handleChangeManagerType = (newManagerType: UserManagerTypesResponse) => {
        managerType.current = newManagerType?.secondaryManagerTypeId;
        pageNumber.current = 1;
        setLoading(true);
    };

    const goPreviousManager = () => {
        if (!manager.current) return;
        var previousManagerIndex = breadrumbs.findIndex(p => p.userId == manager.current!.userId);
        if ((previousManagerIndex - 1) >= 0) {
            handlePreviousManager(breadrumbs[previousManagerIndex - 1]);
        }
    };

    const loadNextPage = (newPage: number) => {
        pageNumber.current = newPage;
        setLoading(true);
    };

    ////////////////////////////////////////////////////////////////////////////
    /// EFFECT's
    ////////////////////////////////////////////////////////////////////////////

    // Ao carregar...
    useEffect(() => {
        async function loadData() {
            try {
                if (breadrumbs.length == 0) {
                    const response = await api.v1.user.userOrganogramManagers({ userId });
                    const { primaryManagers, secondaryManagerTypes } = response.data;
                    setBreadrumbs(primaryManagers);

                    if (secondaryManagerTypes != null) {
                        // Somente gerente secundário.
                        manager.current = secondaryManagerTypes;
                        managerType.current = primaryManagers.length == 0 ? secondaryManagerTypes.managerTypes[0].secondaryManagerTypeId : null;
                    }
                    else if (primaryManagers.length > 0) {
                        // É gerente primário.
                        manager.current = primaryManagers[primaryManagers.length - 1];
                    }
                }

                if (manager.current != null) {
                    var result = await api.v1.user.managerSubordinates({
                        managerId: manager.current.userId,
                        managerType: managerType.current,
                        searchText: "",
                        pageNumber: pageNumber.current,
                        pageSize: 12
                    });
                    setViewData(result.data);
                }

            } catch (e) {
                setViewError(e);
            }

            setLoading(false);
        }

        if (loading) loadData();

    }, [loading]);

    ////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////

    const UserCard = (item: SubordinateDto | OrganogramManagerDto) => {
        return (
            <Card
                sx={{
                    margin: "0 auto",
                    width: AppImageDimension.ThumbnailLg.width,
                    border: (theme) => item.userId == userId ? `solid 1px ${theme.palette.warning.main}` : undefined
                }}
            >
                <Box sx={{ p: 1 }}>
                    <Grid container justifyContent="center" rowSpacing={0.5}>
                        <Grid>
                            <Avatar
                                sx={(theme) => (
                                    {
                                        cursor: "pointer",
                                        height: "inherit",
                                        width: "100px",
                                        backgroundColor: theme.palette.grey[200],
                                        "& > img": { width: "100%" }
                                    }
                                )}
                                src={item.thumbnailUrl}
                                onClick={() => showUser(item.userId)}
                            />
                        </Grid>
                    </Grid>

                    <Grid container justifyContent="center" alignItems="center" spacing={0.5}>
                        <Grid>
                            <MprcListItemTitle onClick={() => showUser(item.userId)}>
                                {item.name}
                            </MprcListItemTitle>
                        </Grid>
                        <Grid>
                            {
                                ("hasManager" in item && item.hasManager) ?
                                    <MprcLink onClick={() => goPreviousManager()}>
                                        <MprcIconFa
                                            icon={faSquareArrowUp}
                                            color="warning"
                                            size="lg"
                                            fixedWidth
                                        />
                                    </MprcLink>
                                    :
                                    ("isManager" in item && item.isManager && manager.current?.userId != item.userId) &&
                                    <MprcLink onClick={() => handleNewManager({ ...item, hasManager: true } as OrganogramManagerDto)}>
                                        <MprcIconFa
                                            icon={faSquareArrowDownRight}
                                            color="highlight"
                                            size="lg"
                                            fixedWidth
                                        />
                                    </MprcLink>
                            }
                        </Grid>
                    </Grid>

                    <Grid container spacing={1} justifyContent="center" alignItems="center">
                        <Grid>
                            <Typography variant="caption" noWrap>
                                {item.jobPositionName ?? <>&nbsp;</>}
                            </Typography>
                        </Grid>
                    </Grid>
                </Box>
            </Card>
        );
    };

    if (!loading && manager.current == null) {
        return <MprcMessageBox message={t("")} onClose={() => onClose(undefined)} />;
    }

    return (
        <Dialog
            open
            fullWidth
            maxWidth="lg"
            onClose={() => onClose(undefined)}
        >
            <MprcDialogTitleClose
                shrinkPadding
                title={t("User.SubordinatesOrgDialog.Title")}
                subtitle={
                    <>
                        <Breadcrumbs
                            maxItems={5}
                        >
                            {
                                breadrumbs.map((v, index) => {
                                    return (index < breadrumbs.length - 1)
                                        ? <MprcLink key={v.userId} onClick={() => handlePreviousManager(v)}><Typography variant="caption" color="info.main">{v.name}</Typography></MprcLink>
                                        : <Typography key={v.userId} variant="caption" color="warning.main">{v.name}</Typography>;
                                })
                            }
                        </Breadcrumbs>

                        <MprcInfiniteLoading visible={loading} />
                    </>
                }
                onClose={() => onClose(undefined)}
            />
            <DialogContent sx={{ height: "100vh", minWidth: 480 }}>
                {
                    viewError
                        ? <MprcRequestErrorReport error={viewError} />
                        : (
                            <>
                                {
                                    manager.current && viewData?.managerTypes &&

                                    <Grid container direction="column" alignContent="center" sx={{ mb: 4 }} rowSpacing={2}>
                                        <Grid>
                                            {UserCard(manager.current)}
                                        </Grid>
                                        <Grid alignSelf="center">
                                            {
                                                viewData?.managerTypes.filter(p => p.secondaryManagerTypeId != NIL).length > 0 &&
                                                viewData?.managerTypes.map((item, index) => (
                                                    <MprcChipChoice
                                                        key={index}
                                                        label={item.secondaryManagerTypeId == NIL ? t("User.SubordinatesOrgDialog.ManagerText") : t("User.SubordinatesOrgDialog.SecondaryManagerText", { name: item.name })}
                                                        selected={item.secondaryManagerTypeId == (managerType.current ?? NIL)}
                                                        sx={{ mb: 0.5, mr: 0.5 }}
                                                        onClick={
                                                            item.secondaryManagerTypeId == (managerType.current ?? NIL)
                                                                ? undefined
                                                                : () => handleChangeManagerType(item)
                                                        }
                                                    />
                                                ))
                                            }
                                        </Grid>
                                    </Grid>
                                }

                                {
                                    viewData?.subordinates &&
                                    <Grid container justifyContent="space-around" rowSpacing={2} columnSpacing={1}>
                                        {
                                            viewData?.subordinates.items.map((item) => (
                                                <Grid size={{ xs: 6, sm: 4, md: 3, lg: 3 }}>
                                                    {UserCard(item)}
                                                </Grid>
                                            ))
                                        }
                                    </Grid>
                                }
                            </>
                        )
                }

            </DialogContent>
            <DialogActions sx={{ justifyContent: "center" }}>
                {
                    (viewData?.subordinates.totalPages ?? 0) > 1 &&
                    <Pagination
                        shape="rounded"
                        color="primary"
                        page={viewData?.subordinates.pageNumber}
                        count={viewData?.subordinates.totalPages}
                        onChange={(e, pageNumber) => loadNextPage(pageNumber)}
                    />
                }
            </DialogActions>
        </Dialog>
    );
};
